import React, { useState } from "react";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectMembers } from "../../features/members/members-slice";
import { selectActivePaymentTypes } from "../../features/payment-types/payment-types-slice";
import {
  createContract,
  updateContract,
} from "../../features/contracts/slice-extensions/contracts-slice-extension";
import { Contract } from "../../features/contracts/models/Contract";
import { ContractType } from "../../features/contracts/models/ContractType";
import { parseMoneyToMinorUnits } from "../../features/billing/utils/money";
import { useMemberNameLookup } from "../../features/members/hooks/useMemberNameLookup";
import PaymentTypeForm from "./PaymentTypeForm";
import { PER_VISIT_ACCOUNT_EXISTS } from "../../features/contracts/models/ContractErrorCodes";

interface ContractFormProps {
  /** When set, the form edits this contract (member / type / start are fixed). */
  contract?: Contract;
  /** Preselects the member on create (quick actions pass the member in need). */
  initialMemberId?: string;
  onSaved: () => void;
  onClose: () => void;
}

/**
 * Create or edit a contract: pack (with size), subscription (with monthly fee + cadence)
 * or per-visit account (no size, fee or cadence — each visit is priced at its payment type).
 */
const ContractForm: React.FC<ContractFormProps> = ({ contract, initialMemberId, onSaved, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const members = useAppSelector(selectMembers);
  const paymentTypes = useAppSelector(selectActivePaymentTypes);
  const memberName = useMemberNameLookup();

  const isEdit = Boolean(contract);
  const [memberId, setMemberId] = useState(contract?.memberId ?? initialMemberId ?? "");
  const [type, setType] = useState<ContractType>(contract?.type ?? ContractType.VisitPack);
  const [paymentTypeId, setPaymentTypeId] = useState(contract?.defaultPaymentTypeId ?? "");
  const [validFrom, setValidFrom] = useState(
    contract?.validFrom ?? new Date().toISOString().slice(0, 10)
  );
  const [validUntil, setValidUntil] = useState(contract?.validUntil ?? "");
  const [packSize, setPackSize] = useState(String(contract?.packSize ?? 10));
  const [monthlyFee, setMonthlyFee] = useState(
    contract?.monthlyFeeAmountMinorUnits != null
      ? (contract.monthlyFeeAmountMinorUnits / 100).toFixed(2)
      : ""
  );
  const [cadence, setCadence] = useState(contract?.cadence ?? "");
  const [notes, setNotes] = useState(contract?.notes ?? "");
  const [error, setError] = useState<string | null>(null);
  const [creatingPaymentType, setCreatingPaymentType] = useState(false);

  const isPack = type === ContractType.VisitPack;
  const isSubscription =
    type === ContractType.FlexibleSubscription || type === ContractType.FlatSubscription;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!memberId || !paymentTypeId || !validFrom) {
      setError(t("billing.contractForm.errorRequired"));
      return;
    }
    const fee = monthlyFee
      ? parseMoneyToMinorUnits(monthlyFee,
          paymentTypes.find((p) => p.id === paymentTypeId)?.currency ?? "")
      : undefined;
    if (isSubscription && (fee === undefined || Number.isNaN(fee))) {
      setError(t("billing.contractForm.errorMonthlyFee"));
      return;
    }
    try {
      if (isEdit && contract) {
        await dispatch(updateContract({
          id: contract.id,
          request: {
            defaultPaymentTypeId: paymentTypeId,
            validUntil: validUntil || undefined,
            packSize: isPack ? Number(packSize) || undefined : undefined,
            monthlyFeeAmountMinorUnits: isSubscription ? fee : undefined,
            cadence: isSubscription && cadence ? cadence : undefined,
            notes: notes || undefined,
          },
        })).unwrap();
      } else {
        await dispatch(createContract({
          memberId,
          type,
          defaultPaymentTypeId: paymentTypeId,
          validFrom,
          validUntil: validUntil || undefined,
          packSize: isPack ? Number(packSize) || undefined : undefined,
          monthlyFeeAmountMinorUnits: isSubscription ? fee : undefined,
          cadence: isSubscription && cadence ? cadence : undefined,
          notes: notes || undefined,
        })).unwrap();
      }
      onSaved();
    } catch (err) {
      const apiError = err as { message?: string; code?: string };
      setError(
        apiError?.code === PER_VISIT_ACCOUNT_EXISTS
          ? t("billing.contractForm.errorPerVisitAccountExists")
          : apiError?.message ?? t("billing.contractForm.errorSave")
      );
    }
  };

  // A fresh account has no prices yet — let the first payment type be created
  // right here, then return with it preselected.
  if (creatingPaymentType) {
    return (
      <PaymentTypeForm
        onSaved={(saved) => {
          setPaymentTypeId(saved.id);
          setCreatingPaymentType(false);
        }}
        onClose={() => setCreatingPaymentType(false)}
      />
    );
  }

  return (
    <form onSubmit={handleSubmit} className="p-4 lg:p-6">
      <h3>
        {isEdit
          ? t("billing.contractForm.editTitle", { member: memberName(contract!.memberId) })
          : t("billing.actions.newContract")}
      </h3>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {!isEdit && (
          <>
            <select className="input" value={memberId} onChange={(e) => setMemberId(e.target.value)}>
              <option value="">{t("billing.contractForm.memberPlaceholder")}</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>{m.firstName} {m.lastName}</option>
              ))}
            </select>
            <select className="input" value={type} onChange={(e) => setType(e.target.value as ContractType)}>
              <option value={ContractType.VisitPack}>{t("billing.contractType.VisitPack")}</option>
              <option value={ContractType.FlexibleSubscription}>{t("billing.contractType.FlexibleSubscription")}</option>
              <option value={ContractType.FlatSubscription}>{t("billing.contractType.FlatSubscription")}</option>
              <option value={ContractType.PerVisitAccount}>{t("billing.contractType.PerVisitAccount")}</option>
            </select>
          </>
        )}
        {paymentTypes.length === 0 ? (
          <div className="sm:col-span-2">
            <p className="text-sm text-grey-30 dark:text-grey-70">
              {t("billing.contractForm.noPrices")}
            </p>
            <button type="button" className="btn btn-secondary mt-2" onClick={() => setCreatingPaymentType(true)}>
              <Plus size={16} />
              <span>{t("billing.actions.newPaymentType")}</span>
            </button>
          </div>
        ) : (
          <select className="input" value={paymentTypeId} onChange={(e) => setPaymentTypeId(e.target.value)}>
            <option value="">{t("billing.contractForm.defaultPaymentTypePlaceholder")}</option>
            {paymentTypes.map((pt) => (
              <option key={pt.id} value={pt.id}>{pt.name}</option>
            ))}
          </select>
        )}
        {isPack && (
          <input className="input" placeholder={t("billing.contractForm.packSizePlaceholder")} inputMode="numeric"
            value={packSize} onChange={(e) => setPackSize(e.target.value)} />
        )}
        {isSubscription && (
          <div className="flex gap-3">
            <input className="input" placeholder={t("billing.contractForm.monthlyFeePlaceholder")} inputMode="decimal"
              value={monthlyFee} onChange={(e) => setMonthlyFee(e.target.value)} />
            <input className="input" placeholder={t("billing.contractForm.cadencePlaceholder")}
              value={cadence} onChange={(e) => setCadence(e.target.value)} />
          </div>
        )}
        {!isPack && !isSubscription && (
          <p className="text-sm text-grey-30 dark:text-grey-70">
            {t("billing.contractType.PerVisitAccountHelp")}
          </p>
        )}
        {!isEdit && (
          <label className="text-sm text-grey-30 dark:text-grey-70">
            {t("billing.table.validFrom")}
            <input type="date" className="input mt-1" value={validFrom}
              onChange={(e) => setValidFrom(e.target.value)} />
          </label>
        )}
        <label className="text-sm text-grey-30 dark:text-grey-70">
          {t("billing.contractForm.validUntilOptional")}
          <input type="date" className="input mt-1" value={validUntil}
            onChange={(e) => setValidUntil(e.target.value)} />
        </label>
        <input className="input sm:col-span-2" placeholder={t("billing.common.notesOptionalPlaceholder")}
          value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>
      {error && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>}
      <div className="mt-4 flex justify-end gap-3">
        <button type="button" className="btn btn-secondary" onClick={onClose}>{t("billing.common.cancel")}</button>
        <button type="submit" className="btn btn-primary">
          {isEdit ? t("billing.common.saveChanges") : t("billing.contractForm.create")}
        </button>
      </div>
    </form>
  );
};

export default ContractForm;
